/* Hexabiome email popup (v2.0, 2026-09-12)
 *
 * Signup goes through Liquid's own {% form 'customer' %} and submits natively.
 * That is not a stylistic choice: Shopify attaches an invisible CAPTCHA to customer
 * forms, and a hand-rolled XHR to /contact is rejected with
 * "400 Missing CAPTCHA token" (measured on this store, 2026-09-07).
 * Shopify posts back to the same page, where the snippet renders either the success
 * step (form.posted_successfully?) or the offer step with form.errors.
 *
 * This file only handles when the popup appears, focus, and remembering the
 * Founder link the success step hands out (hexa-offer.js reads it back on the
 * main PDP so a returning visitor is not left paying full price).
 *
 * ES5 only: 87% of this store's traffic is FB/IG in-app webviews.
 * Every storage access is guarded — private mode throws on localStorage.
 */
(function () {
  'use strict';

  var SHOW_AFTER_MS = 5000; // Marco 2026-09-13: sooner — in-app sessions average ~40s total, ~15s active
  var SNOOZE_DAYS_AFTER_DISMISS = 14;
  var KEY_SUBSCRIBED = 'hexa_popup_subscribed';
  var KEY_DISMISSED = 'hexa_popup_dismissed_until';
  var KEY_FOUNDER_URL = 'hexa_founder_url';

  function store(key, value) {
    try {
      if (value === undefined) return window.localStorage.getItem(key);
      window.localStorage.setItem(key, value);
      return value;
    } catch (e) {
      return null; // private mode, blocked storage, quota — never break the page
    }
  }

  // Mobile QA 2026-09-13 (R7): the popup opened on top of the cart drawer and covered Checkout.
  function drawerOpen() {
    var d = document.getElementById('hexaCartDrawer');
    return !!(d && (' ' + d.className + ' ').indexOf(' is-open ') > -1);
  }

  function suppressedByPath() {
    var p = (window.location.pathname || '').toLowerCase();
    return p.indexOf('/cart') === 0 || p.indexOf('/checkout') === 0 || p.indexOf('/account') === 0;
  }

  function suppressedByHistory() {
    try {
      if (store(KEY_SUBSCRIBED) === '1') return true;
      var until = store(KEY_DISMISSED);
      if (until && Date.now() < parseInt(until, 10)) return true;
    } catch (e) {}
    return false;
  }

  function validEmail(value) {
    var v = (value || '').trim();
    if (v.length < 5 || v.length > 254) return false;
    var at = v.indexOf('@');
    if (at < 1 || at !== v.lastIndexOf('@')) return false;
    var dot = v.indexOf('.', at);
    return dot > at + 1 && dot < v.length - 1 && v.indexOf(' ') === -1;
  }

  // The announcement bar is the Founder Offer door (Marco 2026-09-16): it opens this popup.
  // Someone who already unlocked the offer skips the ask and goes straight to the link.
  function storedFounderUrl() {
    var u = store(KEY_FOUNDER_URL);
    if (!u || u.charAt(0) !== '/' || u.charAt(1) === '/') return null; // same-site path only
    return u;
  }

  function bindFounderCta(open) {
    var nodes = document.querySelectorAll('[data-founder-cta]');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].addEventListener('click', function (e) {
        var url = storedFounderUrl();
        if (url) {
          e.preventDefault();
          window.location.href = url;
          return;
        }
        if (open) {
          e.preventDefault();
          open(); // a deliberate click beats the snooze: the visitor asked for it
        }
        // no popup and no link yet (already-subscribed customer): the href stands
      });
    }
  }

  function boot() {
    var el = document.getElementById('hexaPopup');
    if (!el) {
      bindFounderCta(null); // customer already subscribed, or snippet not rendered
      return;
    }

    var panel = el.querySelector('.hexa-popup-panel');
    var form = el.querySelector('.hexa-popup-form');
    var input = el.querySelector('.hexa-popup-input');
    var submit = el.querySelector('[data-popup-submit]');
    var errorEl = el.querySelector('[data-popup-error]');
    var successStep = el.querySelector('[data-popup-step="success"]');
    var posted = !!(successStep && successStep.getAttribute('data-popup-posted') === 'true');
    var hasErrors = !!(errorEl && !errorEl.hidden && errorEl.textContent.replace(/\s/g, ''));
    var lastFocus = null;
    var open = false;

    function focusable() {
      var nodes = panel.querySelectorAll('button, a[href], input, [tabindex]:not([tabindex="-1"])');
      var out = [];
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].offsetParent !== null && !nodes[i].disabled) out.push(nodes[i]);
      }
      return out;
    }

    function show() {
      if (open) return;
      open = true;
      lastFocus = document.activeElement;
      el.classList.add('is-open');
      document.documentElement.classList.add('hexa-popup-lock');
      var f = focusable();
      if (f.length) f[0].focus();
    }

    function close(reason) {
      if (!open) return;
      open = false;
      el.classList.remove('is-open');
      document.documentElement.classList.remove('hexa-popup-lock');
      if (reason === 'dismissed') {
        store(KEY_DISMISSED, String(Date.now() + SNOOZE_DAYS_AFTER_DISMISS * 86400000));
      }
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    bindFounderCta(show);

    var closers = el.querySelectorAll('[data-popup-close]');
    for (var c = 0; c < closers.length; c++) {
      closers[c].addEventListener('click', function () { close('dismissed'); });
    }

    document.addEventListener('keydown', function (e) {
      if (!open) return;
      var k = e.key || '';
      if (k === 'Escape' || k === 'Esc') {
        close('dismissed');
        return;
      }
      if (k !== 'Tab') return;
      var f = focusable();
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // Client-side check first, so an obvious typo does not cost a page round trip.
    // The native submit must proceed untouched: Shopify's hCaptcha script intercepts
    // the submit event, preventDefaults, solves the challenge, then re-submits the
    // form itself. Two things break that, both measured on this store 2026-09-07:
    //   - form.submit() skips listeners entirely -> "400 Missing CAPTCHA token"
    //   - disabling the submit button stops the captcha's re-submit
    // So we never touch button.disabled; a flag guards double submits instead.
    var submitting = false;
    if (form && input) {
      form.addEventListener('submit', function (e) {
        if (submitting) {
          e.preventDefault();
          return;
        }
        if (!validEmail(input.value)) {
          e.preventDefault();
          if (errorEl) {
            errorEl.textContent = 'Enter a valid email address.';
            errorEl.hidden = false;
          }
          input.focus();
          return;
        }
        submitting = true;
        if (submit) submit.textContent = 'Sending...';
      });
    }


    // Attach Shopify's CAPTCHA to this form.
    // Without it the post is rejected with "400 Missing CAPTCHA token" — Shopify does
    // not auto-protect a theme form that it did not render on a form-bearing page
    // (measured on this store 2026-09-07). Shopify.captcha is lazy, so poll briefly.
    // Note: Shopify.loadFeatures({name:'captcha'}) reports "feature does not exist" on
    // this shop; calling protect() directly is what works.
    (function protectForm(tries) {
      if (!form) return;
      try {
        if (window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function') {
          window.Shopify.captcha.protect(form, function () {});
          return;
        }
      } catch (e) {
        return; // never let CAPTCHA wiring break the page
      }
      if (tries > 40) return; // ~6s, then give up quietly
      setTimeout(function () { protectForm(tries + 1); }, 150);
    })(0);

    // Shopify has just posted back: show the result immediately, whatever it is.
    if (posted) {
      store(KEY_SUBSCRIBED, '1');
      var founderLink = successStep.querySelector('[data-founder-url]');
      if (founderLink) store(KEY_FOUNDER_URL, founderLink.getAttribute('data-founder-url'));
      show();
      return;
    }
    if (hasErrors) {
      show();
      return;
    }

    if (suppressedByPath() || suppressedByHistory()) return;
    // Never open over the cart drawer: wait until it has been closed.
    (function arm(delay) {
      setTimeout(function () {
        if (drawerOpen()) { arm(1500); return; }
        show();
      }, delay);
    })(SHOW_AFTER_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
